import React, { useMemo, useRef, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import api from '../../services/api';

const RichTextEditor = ({
  label,
  value,
  onChange,
  placeholder = 'Write your content here...',
  error,
  minHeight = 200,
  className = '',
  onImageUpload, // Callback for image upload
}) => {
  const quillRef = useRef(null);

  // Custom image handler to upload images instead of base64
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.setAttribute('multiple', 'false');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      try {
        // Show loading state
        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        const range = quill.getSelection(true);
        quill.insertText(range.index, 'Uploading image...');

        // Upload image to server
        const formData = new FormData();
        formData.append('image', file);
        formData.append('type', 'article-content');

        const response = await api.post('/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Remove loading text
        quill.deleteText(range.index, 'Uploading image...'.length);

        // Insert image URL
        const imageUrl = response.data?.data?.url || response.data?.url;
        if (imageUrl) {
          quill.insertEmbed(range.index, 'image', imageUrl);
          quill.setSelection(range.index + 1);
          
          // Callback for tracking uploaded images
          if (onImageUpload) {
            onImageUpload({
              url: imageUrl,
              file: file,
              alt: file.name.replace(/\.[^/.]+$/, ''), // filename without extension
            });
          }
        } else {
          throw new Error('No image URL returned');
        }
      } catch (error) {
        console.error('Image upload failed:', error);
        
        // Remove loading text
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection(true);
          quill.deleteText(range.index, 'Uploading image...'.length);
        }
        
        alert('Failed to upload image. Please try again or use a smaller image.');
      }
    };
  }, [onImageUpload]);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ font: [] }],
        [{ size: ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ direction: 'rtl' }],
        [{ align: [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean'],
      ],
      handlers: {
        image: imageHandler, // Custom image handler
      },
    },
    clipboard: {
      matchVisual: false,
    },
  }), [imageHandler]);

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'indent',
    'direction', 'align',
    'blockquote', 'code-block',
    'link', 'image', 'video',
  ];

  return (
    <div className={`space-y-2 ${className}`}>
      {label && <label className="block text-sm font-medium text-slate-700">{label}</label>}
      <div className={`rounded-xl overflow-hidden ${error ? 'ring-2 ring-red-500' : ''}`}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ''}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          style={{ minHeight }}
        />
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
      <p className="text-xs text-gray-500">
        💡 Tip: Images are automatically uploaded to the server. Max size: 5MB per image.
      </p>
    </div>
  );
};

export default RichTextEditor;
